import React, { useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import { Producer } from "../../../context/wineContext/reducer";
import BaseWineResult from "../BaseWines/BaseWineResult";
import { formatTitle, getParam } from '../../../utils/helper';
import { loadBaseWines, loadInitialWines, loadWines, useWineContext } from "../../../context/wineContext";
import WineVintage from "../Wines/WineVintage";
import { createMarkup } from "../../../utils/helper";
import Pagination from "../Pagination/Pagination";
import Link from "next/link";
import DropDown from "../Inputs/DropDown";
import Head from "next/head";
import { determineSortColumn, generateMetaDescription, getSortOptions } from "../../../utils/producer";
import { useAuth } from "../../../context/authContext";
import { Skeleton } from "../Skeleton/Skeleton";
import { useToast } from "../../../context/toastContext";
import { useValidSubscription } from "../../../hooks/useValidSubscription";
import { lockScoreSortOptions } from "../../../utils/wine";

interface IProducerDetailProps {
  slug: string;
  articleId?: string;
  producer: Producer;
  article: any;
  initialWines?: any;
  initialDisplayType?: string;
  error?: string;
}

const ProducerDetail = ({slug, articleId, producer, article, initialWines, initialDisplayType, error}: IProducerDetailProps) => {
  const router = useRouter();
  const firstLoad = useRef(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [producerCommentary, setProducerCommentary] = useState("");
  const [displayType, setDisplayType] = useState(initialDisplayType || "wine");
  const [openAll, setOpenAll] = useState(true);
  const [sortDirection, setSortDirection] = useState(false);
  const [sort, setSort] = useState({
    label: "Sort by Vintage",
    value: "vintage"
  });
  const [sortOptions, setSortOptions] = useState([]);
  const {state: { loading: loadingUser }} = useAuth();
  const toast = useToast();
  const hasValidSubscription = useValidSubscription();

  useEffect(() => {
    if (error) {
      toast("error", error);
    }
  }, []);

  // Sort by wine (Tasting) ID when filtered by article
  // Otherwise, sort by vintage
  useEffect(() => {
    const sortOptionsConfig = getSortOptions(articleId);
    if (articleId) {
      setSort({
        label: "Sort by default",
        value: "default"
      })
      setSortDirection(true);
    }
    setSortOptions(sortOptionsConfig);
  }, [articleId])

  const {
    state: { baseWines, baseWinesTotal, wines, winesTotal },
    dispatch: winesDispatch,
  } = useWineContext();

  useEffect(() => {
    if (!firstLoad.current && router.isReady) {
      firstLoad.current = true;
      manageQueryParams();
      loadInitialData();
      return;
    }
    loadData();
  }, [displayType, producer, sortDirection, sort, page, perPage])

  useEffect(() => {
    setProducerCommentary("");

    if (slug && articleId && wines) {
      for (const wine of wines) {
        if (wine.producer_commentary) {
          setProducerCommentary(wine.producer_commentary);
        }
      }
    }
  }, [wines, slug, articleId])

  const handlePageChange = (e) => {
    setPage(e.page);
    setPerPage(e.perPage);
  }

  const handleTypeChange = (type) => {
    setPage(1);
    setPerPage(25);
    setDisplayType(type);
  }

  const loadInitialData = () => {
    const dispatchType = displayType === "baseWine" ? "setBaseWines" : "setWines";
    loadInitialWines(initialWines, winesDispatch, dispatchType);
  }

  const manageQueryParams = () => {
    if(slug) {
      router.replace({
        pathname: '/producers/' + slug,
        query: {
          article_id: articleId || "",
          sort: sort.value || "default",
          sortDirection: sortDirection,
          page: page,
          perPage: perPage,
          displayType: displayType
        }
      }, undefined, { shallow: true })
    }
  }

  const loadData = async () => {
    const requestParams = {
      term: "",
      "filters[producer_id]": parseInt(producer?.id),
      "filters[article_id]": articleId && parseInt(articleId),
      vintage: null,
      order: determineSortColumn(displayType, sort),
      order_type: sortDirection ? "asc" : "desc",
      page: page, 
      per_page: perPage
    };

    manageQueryParams();
    
    if(displayType == "baseWine") {
      loadBaseWines(requestParams, winesDispatch);
    } else if (displayType === "wine") {
      loadWines(requestParams, winesDispatch)
    }
  }

  return (
    <div>
      {
        producer && <Head>
          <title>{formatTitle(`${producer?.producer_name}`)}</title>
          <meta name="description" content={generateMetaDescription(producer?.producer_name)}/>
          <meta name="viewport" content="width=device-width, initial-scale=1"/>
          <meta name="robots" content="index, follow" />
          <link rel="canonical" href={`https://vinous.com/producers/${slug}`}/>
        </Head>
      }
      {
        producer &&
        <div>
          <h1 className="text-center text-2xl font-bold pt-6">
            { producer && producer.producer_name }
          </h1>

          <div className="pb-6">
            {
              producerCommentary && 
              <div
                className="font-light text-sm [&>p]:mb-4 [&>p:last-child]:mb-0"
                dangerouslySetInnerHTML={createMarkup(producerCommentary)}
              />
            }
            {
              article && article.id &&
              <Link
                href={`/articles/${article.slug}`}
              >
                <p className="text-sm font-normal pt-2 italic cursor-pointer">
                  From <span className="text-red">{ article.title }</span> by { article.author } 
                </p>
              </Link>
            }
          </div>
        </div>
      }

      <div className="space-y-2">
        <div className="md:flex">
          <div className="flex flex-row align-end w-full">
            <div 
              className={`transition text-xl text-center px-2 py-1 mb-4 mr-8 border-b opacity-70 ${ displayType === "wine" && "border-red opacity-100" } cursor-pointer hover:border-red hover:opacity-100`}
              onClick={() => { handleTypeChange("wine") }}
              onKeyDown={(e) => {
                if(e.key === 'Enter') {
                  handleTypeChange("wine");
                }
              }}
              role="button"
              tabIndex={0}
            >
              Reviews
            </div>
            <div 
              className={`transition text-xl text-center px-2 py-1 mb-4 mr-8 border-b opacity-70 ${ displayType === "baseWine" && "border-red opacity-100" } cursor-pointer hover:border-red hover:opacity-100`}
              onClick={() => { handleTypeChange("baseWine") }}
              onKeyDown={(e) => {
                if(e.key === 'Enter') {
                  handleTypeChange("baseWine");
                }
              }}
              role="button"
              tabIndex={0}
            >
              Wines
            </div>
          </div>
          
          <div className="flex items-center justify-end justify-self-end md:ml-4 md:mr-4 pb-4 w-full xl:w-1/2">
            <DropDown 
              type="line"
              placeholder="Sort By"
              className="w-full"
              options={lockScoreSortOptions(sortOptions, !hasValidSubscription)}
              value={sort.label}
              onOptionSelect={(e) => {
                setSort(e);
              }}
            />
            <div
              className={`transition cursor-pointer ml-5 hover:text-red hover:opacity-100 ${ !sort ? "opacity-20" : "opacity-100" }`}
              onClick={() => {
                setSortDirection(!sortDirection);
              }}
              onKeyDown={(e) => {
                if(e.key === "Enter") {
                  setSortDirection(!sortDirection);
                }
              }}
              role="button"
              tabIndex={0}
              aria-label="Toggle sort direction"
            >
              {
                sortDirection ?
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                </svg>
                :
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4" />
                </svg>
              }
            </div>
            <div 
              className="transition w-4 pl-5 pr-3 cursor-pointer hover:text-red"
              onClick={() => {
                setOpenAll(!openAll);
              }}
              onKeyDown={(e) => {
                if(e.key === "Enter") {
                  setOpenAll(!openAll);
                }
              }}
              role="button"
              tabIndex={0}
              aria-label="Toggle open all wines"
            >
              {
                openAll ?
                <svg width="16px" height="16px" viewBox="0 0 20 20" version="1.1" xmlns="http://www.w3.org/2000/svg" stroke="currentColor">
                    <g id="Page-1" strokeWidth="1" fill="none" fillRule="evenodd" strokeLinecap="round" strokeLinejoin="round">
                        <g id="v" transform="translate(-366.000000, -283.000000)" strokeWidth="2">
                            <g id="Group-Copy" transform="translate(367.000000, 284.000000)">
                                <path d="M12.5,1.5 L12.5,5.5 M12.5,5.5 L16.5,5.5 M12.5,5.5 L17.5,0.5 M5.5,16.5 L5.5,12.5 M5.5,12.5 L1.5,12.5 M5.5,12.5 L0.5,17.5" id="Shape"></path>
                            </g>
                        </g>
                    </g>
                </svg>
                :
                <svg width="16px" height="16px" viewBox="0 0 20 20" version="1.1" xmlns="http://www.w3.org/2000/svg" stroke="currentColor">
                    <g id="Page-1" strokeWidth="1" fill="none" fillRule="evenodd" strokeLinecap="round" strokeLinejoin="round">
                        <g id="v" transform="translate(-327.000000, -284.000000)" strokeWidth="2">
                            <g id="Group" transform="translate(327.000000, 284.000000)">
                                <path d="M17,5 L17,1 M17,1 L13,1 M17,1 L12,6 M1,13 L1,17 M1,17 L5,17 M1,17 L6,12" id="Shape"></path>
                            </g>
                        </g>
                    </g>
                </svg>
              }
            </div>
          </div>
        </div>

        {
          loadingUser ? (
            <div className="flex no-print flex-row justify-end p-2">
              <div className="w-full" >
                <Skeleton
                  className="w-full"
                  lineHeight="2"
                  titleLineStyle="h-4 mt-4"
                  lines={0}
                />
                <Skeleton
                  className="w-full"
                  lineHeight="1"
                  titleLineStyle="h-2 mt-4 w-20"
                  lines={0}
                />
                <Skeleton
                  className="max-w-2xl w-full"
                  lineHeight="2"
                  titleLineStyle="h-2 mt-4 w-0"
                  lines={4}
                />
                <Skeleton
                  className="w-full"
                  lineHeight="1"
                  titleLineStyle="h-2 mt-4 w-20"
                  lines={0}
                />
              </div>
            </div>
          ) : (
            <>
              {displayType === "baseWine" && (
                <div>
                  {baseWines &&
                    baseWines.map((bw) => {
                      return <BaseWineResult key={bw.slug} baseWine={bw} />;
                    })}

                  {baseWines && baseWinesTotal > 25 && (
                    <Pagination
                      records={baseWinesTotal}
                      onPageChange={(e) => handlePageChange(e)}
                    />
                  )}
                </div>
              )}

              {displayType === "wine" && (
                <div>
                  {wines &&
                    wines.map((wine) => {
                      return (
                        <WineVintage
                          key={wine.id}
                          wine={wine}
                          open={openAll}
                          useBaseWineId
                        />
                      );
                    })}

                  {wines && winesTotal > 25 && (
                    <Pagination
                      records={winesTotal}
                      onPageChange={(e) => handlePageChange(e)}
                    />
                  )}
                </div>
              )}
            </>
          )

        /* {
          displayType === "wineVintage" &&
          <div>
            { wineVintages &&
              wineVintages.map(bw => {
                return (
                  <WineVintage 
                    wine={bw.wines.filter(wine => wine.vintage === bw.vintage)[0]}
                    slug={bw.base_wine.slug}
                    producerName={bw.base_wine.producer.producer_name}
                  /> 
                )
              })
            }

            {
              wineVintages &&
              wineVintagesTotal > 25 &&
              <Pagination 
                records={wineVintagesTotal}
                onPageChange={(e) => handlePageChange(e)}
              />
            }
          </div>
        } */}
      </div>
    </div>
  )
}

export default ProducerDetail;
